import React from "react"
import { Alert, Badge, Button, Progress, Spinner } from "reactstrap"
import { formatearNumero } from "helpers/formatters"
import {
  ESTADO,
  TEXTOS,
  formatearFechaART,
  colorEstado,
  esEstadoFinal,
  etiquetaEstadoLimpieza,
  etiquetaMotivo,
  excluirArcaReflejado,
  formatearDuracion,
  formatearPorcentaje,
  infoEstadoPaso,
  modoLimpieza,
  motivoPaso,
  nombrePaso,
  porcentajePaso,
  recalculoScoringCortado,
  rotuloEstado,
  TEXTO_BORRADO_INCIERTO,
  textoEtapa,
  textoContinuarScoring,
  textoExitoConExcluidos,
  textoHace,
} from "./migracionEstadoHelpers"
import {
  NO_EJECUTADO,
  derivarResumenPorPasos,
  esCorridaPorNiveles,
} from "./resumenPasos"

const num = n => formatearNumero(typeof n === "number" ? n : 0)

const lista = v => (Array.isArray(v) ? v : [])
const objeto = v => (v && typeof v === "object" ? v : {})

// Barra accesible. El role="progressbar" es el elemento interno: por eso el
// nombre accesible (aria-label) y el texto de valor van sobre ese elemento
// (Progress multi + Progress bar). Sin valor (value == null) la barra es
// indeterminada: se arma a mano SIN aria-valuenow, solo con aria-valuetext.
const Barra = ({
  label,
  value = null,
  color = "primary",
  altura = 8,
  animated = false,
}) => {
  if (value == null) {
    return (
      <div className="progress" style={{ height: altura }}>
        <div
          role="progressbar"
          aria-label={label}
          aria-valuetext="En curso"
          className={`progress-bar progress-bar-striped progress-bar-animated bg-${color}`}
          style={{ width: "100%" }}
        />
      </div>
    )
  }
  return (
    <Progress multi style={{ height: altura }}>
      <Progress
        bar
        value={value}
        color={color}
        animated={animated}
        striped={animated}
        aria-label={label}
        barAriaValueText={formatearPorcentaje(value)}
      />
    </Progress>
  )
}

const Contadores = ({
  contadores,
  className = "",
  conContadoresProductores = true,
}) => {
  const c = objeto(contadores)
  // BDSJEA-441: en una corrida sin Productores estos contadores son de
  // productores y no aplican: no se muestran.
  const principales = conContadoresProductores
    ? [
        ["Creados", c.creados, "success"],
        ["Actualizados", c.actualizados, "primary"],
        ["Excluidos", c.excluidos, "info"],
        ["Errores", c.errores, "danger"],
      ]
    : []
  const extras = [
    ["Ya existían", c.yaExistian],
    ["Descartados", c.descartados],
    ["Sincronizados", c.sincronizados],
    ["Scorings calculados", c.scoringsCalculados],
    ["Polígonos aproximados creados", c.poligonosAproximados],
    ["Lotes sin coordenada", c.lotesSinCoordenada],
    ["Establecimientos con nivel de rendimiento", c.nivelesRendimiento],
    ["Establecimientos sin nivel de rendimiento", c.sinNivelRendimiento],
    ["Sin datos", c.sinDatos],
    ["Reintentos", c.reintentos],
    ["Reconciliadas", c.reconciliadas],
  ].filter(([, v]) => typeof v === "number" && v > 0)
  return (
    <div className={className}>
      <div className="d-flex flex-wrap gap-2">
        {principales.map(([titulo, valor, color]) => (
          <Badge
            key={titulo}
            color={color}
            className="fs-6 fw-normal"
            data-testid={`contador-${titulo}`}
          >
            {titulo}: {num(valor)}
            {/* Los repetidos están INCLUIDOS en los excluidos: no suman. */}
            {titulo === "Excluidos" &&
              typeof c.repetidos === "number" &&
              c.repetidos > 0 &&
              ` (de ellos ${num(c.repetidos)} repetidos)`}
          </Badge>
        ))}
      </div>
      {extras.length > 0 && (
        <small className="text-muted d-block mt-1">
          {extras.map(([t, v]) => `${t}: ${num(v)}`).join(" · ")}
        </small>
      )}
    </div>
  )
}

// «Scorings calculados: 242 · Fallidos: 8»; vacío si el paso no corrió.
const textoScoring = paso => {
  if (!["EN_CURSO", "OK", "ERROR"].includes(paso?.estado)) return ""
  const c = objeto(paso?.contadores)
  const calculados = typeof c.scoringsCalculados === "number"
  const fallidos = typeof c.errores === "number" && c.errores > 0
  if (!calculados && !fallidos) return ""
  return [
    calculados && `Scorings calculados: ${num(c.scoringsCalculados)}`,
    fallidos && `Fallidos: ${num(c.errores)}`,
  ]
    .filter(Boolean)
    .join(" · ")
}

const FilaEtapa = ({ paso, actual }) => {
  const info = infoEstadoPaso(paso?.estado)
  const pct = porcentajePaso(paso)
  const enCurso = paso?.estado === "EN_CURSO"
  return (
    <li
      className={`list-group-item py-2 ${actual ? "bg-light" : ""}`}
      data-testid="etapa"
    >
      <div className="d-flex justify-content-between align-items-center">
        <span>
          <i className={`mdi ${info.icono} ${info.color} me-2`} />
          <strong>{nombrePaso(paso)}</strong>
          {enCurso && typeof paso?.filasTotal === "number" && (
            <span className="ms-2 text-muted">{textoEtapa(paso)}</span>
          )}
        </span>
        <small className={info.color}>{info.texto}</small>
      </div>
      {enCurso && (
        <div className="mt-1">
          <Barra label={`Avance de ${nombrePaso(paso)}`} value={pct} />
        </div>
      )}
      {paso?.estado === "OMITIDO" && motivoPaso(paso) && (
        <small className="text-muted d-block mt-1" data-testid="motivo-etapa">
          {motivoPaso(paso)}
        </small>
      )}
      {/* BDSJEA-454: avance del recálculo de scoring (fallidos = `errores`). */}
      {paso?.id === "recalcular_scoring" && textoScoring(paso) && (
        <small
          className="text-muted d-block mt-1"
          data-testid="contadores-scoring"
        >
          {textoScoring(paso)}
        </small>
      )}
    </li>
  )
}

// BDSJEA-353: resumen de la limpieza completa (bloque aditivo `limpiezaCompleta`
// del estado). Todo defensivo: cualquier campo puede faltar.
const ResumenLimpieza = ({ bloque }) => {
  const modo = modoLimpieza(bloque)
  const ensayo = bloque.modo === "ENSAYO"
  const porTabla = lista(bloque.porTabla).filter(
    t => t && typeof t === "object",
  )
  const c = objeto(bloque.constraints)
  const hayConstraints = typeof c.total === "number"
  return (
    <div className="mb-3 small" data-testid="resumen-limpieza">
      <strong className="d-block">Limpieza completa de la base</strong>
      <ul className="mb-1">
        {bloque.ambiente && <li>Ambiente: {bloque.ambiente}</li>}
        {bloque.estado && (
          <li>Estado: {etiquetaEstadoLimpieza(bloque.estado)}</li>
        )}
        {modo && <li>Modo: {modo}</li>}
        {typeof bloque.filasBorradas === "number" && (
          <li>
            {ensayo ? "Filas que se borrarían" : "Filas borradas"}:{" "}
            {num(bloque.filasBorradas)}
          </li>
        )}
        {bloque.borradoConfirmado === null && (
          <li
            className="text-danger fw-bold"
            data-testid="limpieza-borrado-incierto"
          >
            {TEXTO_BORRADO_INCIERTO}
          </li>
        )}
        {bloque.puntoRestauracionUtc && (
          <li>
            Punto de restauración:{" "}
            {formatearFechaART(bloque.puntoRestauracionUtc)}
          </li>
        )}
        {hayConstraints && (
          <li data-testid="limpieza-constraints">
            Restricciones rehabilitadas: {num(c.rehabilitadas)} de{" "}
            {num(c.total)}
            {c.conError > 0 && ` · con error: ${num(c.conError)}`}
          </li>
        )}
      </ul>
      {porTabla.length > 0 && (
        <details>
          <summary>Detalle por tabla ({num(porTabla.length)})</summary>
          <ul className="mb-0">
            {porTabla.map((t, i) => (
              <li key={`${i}-${t.objeto}`}>
                {t.objeto}: {num(t.borradas)}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  )
}

/**
 * BDSJEA-341: panel de progreso de la Importación Masiva. Presentacional:
 * recibe el estado ya resuelto por el hook de polling.
 *
 * @param {object|null} estado       respuesta de GET /estado (puede ser null)
 * @param {number}      segundosLocales cronómetro local (antes de la 1.ª respuesta)
 * @param {string}      errorConsulta   mensaje si no se pudo consultar el estado
 * @param {string}      avisoSse        aviso/mensaje de la salida en vivo
 * @param {boolean}     sinNoticias     superó el umbral sin información
 * @param {function}    onReintentar
 * @param {boolean}     excluirArcaPedido se pidió "Excluir ARCA" al iniciar
 */
const ProgresoImportacion = ({
  estado,
  segundosLocales = 0,
  errorConsulta = null,
  avisoSse = null,
  sinNoticias = false,
  onReintentar,
  excluirArcaPedido = false,
}) => {
  const ef = estado?.estadoEfectivo || ESTADO.INICIANDO
  const monitor = objeto(estado?.monitor)
  const final = esEstadoFinal(ef)
  const pasos = lista(estado?.pasos)
  const global = objeto(estado?.contadoresGlobales)
  const error = objeto(estado?.error)
  const artefactos = objeto(estado?.artefactos)
  const motivos = Object.entries(
    objeto(
      pasos
        .map(p => objeto(p?.excluidosPorMotivo))
        .reduce((acc, m) => {
          Object.entries(m).forEach(([k, v]) => {
            acc[k] = (acc[k] || 0) + (typeof v === "number" ? v : 0)
          })
          return acc
        }, {}),
    ),
  )
  const logs = pasos
    .map(p => ({ nombre: nombrePaso(p), log: objeto(p?.artefactos).log }))
    .filter(l => l.log)
  const csv = artefactos.excluidosCsv || null
  const pasoActual = pasos.find(p => p?.estado === "EN_CURSO")
  // BDSJEA-441: al terminar una corrida por niveles, los niveles que no tuvieron
  // ningún paso se muestran «No ejecutado» (derivado, nunca persistido).
  const noEjecutados =
    final && esCorridaPorNiveles(estado)
      ? derivarResumenPorPasos(estado).filter(f => f.estado === NO_EJECUTADO)
      : []
  // Corrida por niveles sin Productores: los contadores de productores no
  // aplican. Sin `niveles` (corrida histórica) se muestran siempre.
  const incluyeProductores =
    !esCorridaPorNiveles(estado) ||
    estado.parametros.niveles.includes("productores")
  const pctGlobal =
    typeof estado?.progresoGlobalPct === "number"
      ? Math.min(100, Math.max(0, estado.progresoGlobalPct))
      : null
  const transcurrido =
    typeof monitor.transcurridoSeg === "number"
      ? monitor.transcurridoSeg
      : ef === ESTADO.INICIANDO || !estado
        ? segundosLocales
        : null
  const enCurso = ef === ESTADO.EN_CURSO
  const barraColor = colorEstado(ef) === "info" ? "primary" : colorEstado(ef)

  const mensajeBe = estado?.mensaje || error.mensaje || null
  const excluidos = typeof global.excluidos === "number" ? global.excluidos : 0

  return (
    <div data-testid="progreso-importacion">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <span>
          <Badge color={colorEstado(ef)} data-testid="badge-estado">
            {rotuloEstado(ef)}
          </Badge>
          {!final && ef !== ESTADO.SIN_RESPUESTA && (
            <Spinner size="sm" color="primary" className="ms-2" />
          )}
        </span>
        <small className="text-muted" data-testid="transcurrido">
          Tiempo transcurrido: {formatearDuracion(transcurrido)}
        </small>
      </div>

      {/* ---- Alertas de estado ---- */}
      {ef === ESTADO.INICIANDO && (
        <Alert color="info" fade={false} className="py-2">
          {TEXTOS.iniciando} ({num(segundosLocales)} s)
        </Alert>
      )}
      {ef === ESTADO.COMPLETADA && (
        <Alert color="success" fade={false}>
          <i className="mdi mdi-check-circle-outline me-2" />
          {TEXTOS.exito}
        </Alert>
      )}
      {ef === ESTADO.COMPLETADA_CON_EXCLUIDOS && (
        <Alert color="success" fade={false}>
          <i className="mdi mdi-check-circle-outline me-2" />
          {textoExitoConExcluidos(excluidos)}
        </Alert>
      )}
      {(ef === ESTADO.ERROR || ef === ESTADO.INTERRUMPIDA) && (
        <Alert color="danger" fade={false}>
          <i className="mdi mdi-alert-circle-outline me-2" />
          <strong>{TEXTOS.errorGenerico}</strong>
          {[estado?.mensaje, error.mensaje]
            .filter((t, i, a) => t && a.indexOf(t) === i)
            .map(t => (
              <div key={t}>{t}</div>
            ))}
        </Alert>
      )}
      {/* BDSJEA-454: tras un corte del recálculo no se ofrece reintentar. */}
      {recalculoScoringCortado(estado) && (
        <Alert color="warning" fade={false} data-testid="aviso-scoring-cortado">
          <i className="mdi mdi-information-outline me-2" />
          {textoContinuarScoring(estado.corrida)}
        </Alert>
      )}
      {ef === ESTADO.SIN_RESPUESTA && (
        <Alert color="danger" fade={false}>
          <i className="mdi mdi-alert-circle-outline me-2" />
          {mensajeBe || TEXTOS.sinRespuesta}
        </Alert>
      )}
      {ef === ESTADO.SIN_ARRANQUE && (
        <Alert color="danger" fade={false}>
          <i className="mdi mdi-alert-circle-outline me-2" />
          {mensajeBe || TEXTOS.sinArranque}
        </Alert>
      )}
      {ef === ESTADO.DESCONOCIDO && (
        <Alert color="warning" fade={false}>
          <i className="mdi mdi-alert-outline me-2" />
          {mensajeBe || TEXTOS.desconocido}
        </Alert>
      )}
      {enCurso && monitor.sinAvance === true && (
        <Alert color="warning" fade={false} className="py-2">
          <i className="mdi mdi-alert-outline me-2" />
          {TEXTOS.sinAvance}
          {typeof monitor.segundosDesdeUltimoAvance === "number" &&
            ` Último avance ${textoHace(monitor.segundosDesdeUltimoAvance)}.`}
        </Alert>
      )}
      {/* BDSJEA-357: aviso no bloqueante. No usa <Alert> de reactstrap porque
          fuerza role="alert" (asertivo); acá es un aviso cortés (status). */}
      {enCurso && monitor.degradado === true && (
        <div
          className="alert alert-warning py-2"
          role="status"
          data-testid="aviso-degradado"
        >
          <i className="mdi mdi-alert-outline me-2" aria-hidden="true" />
          {TEXTOS.seguimientoDegradado}
        </div>
      )}
      {/* Aviso no bloqueante (mismo estilo que el de seguimiento degradado).
          Con parametros.excluirArca confirmado y la corrida en curso nunca se
          avisa aunque el paso ARCA siga PENDIENTE (falso positivo del rolling). */}
      {excluirArcaPedido &&
        estado &&
        ef !== ESTADO.INICIANDO &&
        (final || estado.parametros?.excluirArca !== true) &&
        !excluirArcaReflejado(estado) && (
          <div
            className="alert alert-warning py-2"
            role="status"
            data-testid="aviso-excluir-arca"
          >
            <i className="mdi mdi-alert-outline me-2" aria-hidden="true" />
            {TEXTOS.excluirArcaNoReflejado}
          </div>
        )}
      {sinNoticias && !final && (
        <Alert color="danger" fade={false} className="py-2">
          <i className="mdi mdi-alert-circle-outline me-2" />
          {TEXTOS.sinNoticias}
        </Alert>
      )}
      {errorConsulta && (
        <Alert
          color="warning"
          fade={false}
          className="py-2 d-flex justify-content-between align-items-center"
        >
          <span>
            <i className="mdi mdi-wifi-alert me-2" />
            {TEXTOS.consultaFallida}: {errorConsulta}
          </span>
          {onReintentar && (
            <Button size="sm" color="warning" outline onClick={onReintentar}>
              Reintentar
            </Button>
          )}
        </Alert>
      )}
      {avisoSse && (
        <Alert color="warning" fade={false} className="py-2">
          <i className="mdi mdi-alert-outline me-2" />
          {avisoSse}
        </Alert>
      )}

      {/* ---- Barra global ---- */}
      {estado && ef !== ESTADO.INICIANDO && (
        <div className="mb-3">
          <div className="d-flex justify-content-between">
            <small className="text-muted">Avance global (estimado)</small>
            <small data-testid="pct-global">
              {formatearPorcentaje(
                ef === ESTADO.COMPLETADA ||
                  ef === ESTADO.COMPLETADA_CON_EXCLUIDOS
                  ? 100
                  : pctGlobal,
              )}
            </small>
          </div>
          <Barra
            label="Avance global"
            color={barraColor}
            altura={14}
            animated={enCurso}
            value={
              ef === ESTADO.COMPLETADA || ef === ESTADO.COMPLETADA_CON_EXCLUIDOS
                ? 100
                : pctGlobal == null && !final
                  ? null
                  : pctGlobal || 0
            }
          />
        </div>
      )}
      {ef === ESTADO.INICIANDO && (
        <div className="mb-3">
          <Barra label="Iniciando importación" altura={12} />
        </div>
      )}

      {/* ---- Texto de etapa ---- */}
      {enCurso && pasoActual && (
        <p className="mb-2" data-testid="texto-etapa">
          {typeof estado?.pasoActual === "number" &&
            typeof estado?.pasosTotal === "number" && (
              <>
                <strong>
                  Etapa {num(estado.pasoActual)} de {num(estado.pasosTotal)}
                </strong>{" "}
                -{" "}
              </>
            )}
          {textoEtapa(pasoActual)}
          {porcentajePaso(pasoActual) != null &&
            ` (${formatearPorcentaje(porcentajePaso(pasoActual))})`}
        </p>
      )}

      {/* ---- Señal de vida ---- */}
      {enCurso && (
        <p className="text-muted small mb-2" data-testid="senal-de-vida">
          <i
            className={`mdi mdi-heart-pulse me-1 ${
              monitor.sinAvance ? "text-warning" : "text-success"
            }`}
          />
          Último avance: {textoHace(monitor.segundosDesdeUltimoAvance)} · Última
          actualización del servicio:{" "}
          {textoHace(monitor.segundosDesdeActualizado)}
        </p>
      )}

      {/* ---- Etapas ---- */}
      {pasos.length > 0 && (
        <ul className="list-group mb-3">
          {pasos.map((p, i) => (
            <FilaEtapa
              key={`${i}-${p?.id ?? p?.orden ?? ""}`}
              paso={p}
              actual={p?.orden != null && p.orden === estado?.pasoActual}
            />
          ))}
          {noEjecutados.map(f => (
            <li
              key={`no-ejecutado-${f.nivel}`}
              className="list-group-item py-2"
              data-testid="etapa-no-ejecutada"
            >
              <div className="d-flex justify-content-between align-items-center">
                <span>
                  <i
                    className={`mdi ${infoEstadoPaso(NO_EJECUTADO).icono} text-muted me-2`}
                  />
                  <strong>{f.etiqueta}</strong>
                </span>
                <small className="text-muted">
                  {infoEstadoPaso(NO_EJECUTADO).texto}
                </small>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* ---- Limpieza completa (BDSJEA-353) ---- */}
      {estado?.limpiezaCompleta &&
        typeof estado.limpiezaCompleta === "object" && (
          <ResumenLimpieza bloque={estado.limpiezaCompleta} />
        )}

      {/* ---- Contadores ---- */}
      {estado && ef !== ESTADO.INICIANDO && (
        <Contadores
          contadores={global}
          className="mb-3"
          conContadoresProductores={incluyeProductores}
        />
      )}

      {/* ---- Excluidos por motivo ---- */}
      {motivos.length > 0 && (
        <div className="mb-3" data-testid="excluidos-motivo">
          <small className="text-muted d-block">Excluidos por motivo</small>
          <ul className="mb-0 small">
            {motivos.map(([clave, cant]) => (
              <li key={clave}>
                {etiquetaMotivo(clave)}: {num(cant)}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ---- Archivos de la corrida ---- */}
      {estado &&
        (estado.carpeta ||
          estado.corrida ||
          artefactos.resumen ||
          csv ||
          logs.length > 0) && (
          <div className="small text-muted" data-testid="artefactos">
            {(estado.carpeta || estado.corrida) && (
              <div>
                Carpeta de la corrida:{" "}
                <code>
                  {estado.carpeta || `migracion-bsj/${estado.corrida}`}
                </code>
              </div>
            )}
            {formatearFechaART(estado.corrida) !== "-" && (
              <div data-testid="corrida-fecha">
                Corrida iniciada: {formatearFechaART(estado.corrida)}
              </div>
            )}
            {logs.map(l => (
              <div key={l.log}>
                Log ({l.nombre}): <code>{l.log}</code>
              </div>
            ))}
            {artefactos.resumen && (
              <div>
                Resumen: <code>{artefactos.resumen}</code>
              </div>
            )}
            {csv && (
              <div>
                CSV de excluidos: <code>{csv}</code>
              </div>
            )}
          </div>
        )}
    </div>
  )
}

export default ProgresoImportacion
